import type { ContextMenuItemProps } from '@halo/ui-chat-react';
import {
  ChatList,
  ChatSidebar,
  ContextMenu,
  DeleteSmallIcon,
  toaster,
  UnPinIcon,
  Pinicon as PinIcon,
  ReadIcon,
  UnReadIcon,
  SessionCollapseIcon,
  SessionUnCollapseIcon,
  ClearAllLineIcon,
  ConfirmDialog,
  tools,
  MuteChatIcon,
  UnMuteChatIcon,
} from '@halo/ui-chat-react';
import { Box } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router';
import { size, gt, includes, throttle } from 'lodash';
import { AccountType, GroupMemberRole, StateEnum } from '@halo/chat-sdk';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Session } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import {
  DefaultCollapseGroupId,
  isSessionCategory,
  SessionsStatus,
  useAppStore,
  SessionCategoryPrefix,
  getSessionUnreadCount,
} from '@/store/client';
import Paths from '@/configs/router/paths';
import { ChatLinesEnum, useSettingsStore } from '@/store/settings';
import { useSessionStore } from '@/store/session';
import { PinnedList } from '@/components/PinnedList';
import { EmptyContact } from '../../contacts/EmptyContact';
import { SessionItem } from './session-item';

export default memo(function SessionList() {
  const { t } = useTranslation();
  const {
    client,
    activatedSessionId,
    user,
    sessionsExpanded,
    sessionsCollapsed,
    activeSession,
    sessionsStatus,
    packageErrorMap,
    pinnedSessions,
    mediaActivatedId,
    changePinnedList,
    getChatStatus,
  } = useAppStore(
    useShallowEnhance([
      'client',
      'activatedSessionId',
      'user',
      'sessionsExpanded',
      'sessionsCollapsed',
      'activeSession',
      'sessionsStatus',
      'packageErrorMap',
      'pinnedSessions',
      'changePinnedList',
      'mediaActivatedId',
      'getChatStatus',
    ])
  );

  const { connecting, sessionsLoading, setRetryConnecting } = useSessionStore(
    useShallowEnhance(['connecting', 'sessionsLoading', 'setRetryConnecting'])
  );
  const navigate = useNavigate();
  const { chat_id } = useParams();

  const { fetchProfileUser, setClearChatHistoryIsOpen, setDeleteChatIsOpen } = useActionsStore(
    useShallowEnhance(['fetchProfileUser', 'setClearChatHistoryIsOpen', 'setDeleteChatIsOpen'])
  );
  const chatLines = useSettingsStore((store) => store.chatLines);
  const [showCollapseChat, setShowCollapseChat] = useState(false);
  const [itemContentHeight, setItemContentHeight] = useState('auto');
  const pageSizeRef = useRef(0);

  const sessions = useMemo(() => {
    return showCollapseChat ? sessionsCollapsed : sessionsExpanded;
  }, [showCollapseChat, sessionsExpanded, sessionsCollapsed]);

  const myPackageError = useMemo(() => {
    return packageErrorMap[user?.user_id];
  }, [packageErrorMap, user?.user_id]);

  const [collapseConfirm, setCollapseConfirm] = useState<{
    open: boolean;
    session: Session | null;
  }>({
    open: false,
    session: null,
  });

  const collapseChatItemActivated = useMemo(() => {
    const has = sessionsCollapsed.find((session) => session.chat_id === activatedSessionId);
    return !!has;
  }, [activatedSessionId, sessionsCollapsed]);

  const getItemsData = useCallback(
    async (data: { session: Session }) => {
      const { session } = data;
      const isCollapseChat = session && session.chat_id === DefaultCollapseGroupId;
      const isCollapsed = !!session.collapse_group_id;
      const isMuted = session.operateAccess?.MUTED;
      const isGroup = isSessionCategory(session?.chat_id, SessionCategoryPrefix.Group);
      // const isRobot = !isGroup && session?.user_info?.account_type === AccountType.Robot;
      const isDeletedAccount = !isGroup && session?.user_info?.state === StateEnum.DELETE_ACCOUNT;
      const { unread_count, muted_unread_count } = getSessionUnreadCount(session);
      const hasUnread = unread_count > 0 || muted_unread_count > 0;

      let isGroupError = false;
      let items: ContextMenuItemProps<any>[] = [];

      if (isGroup) {
        try {
          const result = await client?.Api.session.getMyGroupRole(session.chat_id);
          if (result?.role === GroupMemberRole.NONE) {
            isGroupError = true;
          }
        } catch (_error) {
          // fixed no need toaster
          isGroupError = true;
          // tools.toaster.error({
          //   title: (error as Error)?.message || 'error',
          // });
        }
      }

      items = [
        {
          value: 'collapse',
          danger: false,
          hidden: isCollapseChat || isCollapsed || !isMuted || isDeletedAccount,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {t('chatSettings.moveToCollapsedChats')}
            </Box>
          ),
          icon: (
            <Box>
              <SessionCollapseIcon />
            </Box>
          ),
          onItemClick: async () => {
            if (!sessionsCollapsed.length) {
              setCollapseConfirm({ open: true, session });
              return;
            }
            try {
              await changePinnedList(session?.chat_id, false, false);
              await client?.Api.session.setSessionCollapse(
                session.chat_id,
                'default_collapse_group'
              );
              tools.toaster.success({
                title: t('chatSettings.chatCollapsed'),
              });
            } catch (error) {
              tools.toaster.error({
                title: (error as Error)?.message || t('tips.error'),
              });
            }
          },
        },
        {
          value: 'unCollapse',
          danger: false,
          hidden: isCollapseChat || !isCollapsed || isDeletedAccount,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {t('chatSettings.moveToActiveChats')}
            </Box>
          ),
          icon: (
            <Box>
              <SessionUnCollapseIcon />
            </Box>
          ),
          onItemClick: async () => {
            try {
              await client?.Api.session.setSessionCollapse(session.chat_id, '');
              tools.toaster.success({
                title: t('chatSettings.chatExpanded'),
              });
            } catch (error) {
              tools.toaster.error({
                title: (error as Error)?.message || t('tips.error'),
              });
            }
          },
        },
        {
          value: 'mute',
          danger: false,
          hidden: isCollapseChat || isMuted || isDeletedAccount,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {t('chatSettings.mute')}
            </Box>
          ),
          icon: (
            <Box>
              <MuteChatIcon />
            </Box>
          ),
          onItemClick: async () => {
            try {
              await client?.Api.session.setSessionMuted(session.chat_id, true);
              tools.toaster.success({
                title: t('chatSettings.muted'),
              });
            } catch (error) {
              tools.toaster.error({
                title: (error as Error)?.message || t('tips.error'),
              });
            }
          },
        },
        {
          value: 'unmute',
          danger: false,
          hidden: isCollapseChat || !isMuted || isDeletedAccount,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {t('chatSettings.unmute')}
            </Box>
          ),
          icon: (
            <Box>
              <UnMuteChatIcon />
            </Box>
          ),
          onItemClick: async () => {
            try {
              await client?.Api.session.setSessionCollapse(session!.chat_id, '');
              isGroup &&
                (await client?.Api.session.setSessionAtAllMuted(session!.chat_id, false));
              await client?.Api.session.setSessionMuted(session.chat_id, false);
              tools.toaster.success({
                title: t('chatSettings.notMuted'),
              });
            } catch (error) {
              tools.toaster.error({
                title: (error as Error)?.message || t('tips.error'),
              });
            }
          },
        },
        {
          value: 'readMark',
          hidden: isCollapseChat || isDeletedAccount,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {hasUnread ? t('chatSettings.markAsRead') : t('chatSettings.markAsUnread')}
            </Box>
          ),
          icon: <Box>{hasUnread ? <ReadIcon /> : <UnReadIcon />}</Box>,
          onItemClick: () => {
            try {
              if (hasUnread) {
                client?.Api.session.setSessionAllRead(session?.chat_id);
                client?.Api.session.setSessionReadMark(session?.chat_id, false);
              } else {
                client?.Api.session.setSessionReadMark(session?.chat_id, true);
              }
            } catch (error) {
              tools.toaster.error({
                title: (error as Error)?.message || 'error',
              });
            }
          },
        },
        {
          value: 'pinned',
          hidden: isCollapseChat || isCollapsed || isDeletedAccount,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {!includes(pinnedSessions, session.chat_id)
                ? t('chatSettings.pinToTop')
                : t('chatSettings.unpinFromTop')}
            </Box>
          ),
          icon: (
            <Box>
              {pinnedSessions.find((id) => id === session.chat_id) ? <UnPinIcon /> : <PinIcon />}
            </Box>
          ),
          onItemClick: async () => {
            const is_pinned = !includes(pinnedSessions, session.chat_id);
            if (is_pinned && gt(size(pinnedSessions), 8)) {
              toaster.warning({
                title: t('chatSettings.pinnedChatsLimitReached'),
              });
              return;
            }
            changePinnedList(session.chat_id, is_pinned, isCollapsed)
              .then(() => {
                toaster.success({
                  title: is_pinned ? t('chatSettings.pinned') : t('chatSettings.unpinned'),
                });
              })
              .catch((error) => {
                toaster.error({
                  title: error,
                });
              });
          },
        },
        {
          value: 'clearHistory',
          hidden: isCollapseChat,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {t('chatSettings.clearAllChatHistory')}
            </Box>
          ),
          icon: (
            <Box>
              <ClearAllLineIcon />
            </Box>
          ),
          onItemClick: async () => {
            setClearChatHistoryIsOpen(true, {
              chat_id: session.chat_id,
            });
          },
        },
        {
          value: 'delete',
          danger: true,
          hidden: isCollapseChat,
          label: (
            <Box fontSize="sm" fontWeight="semibold">
              {t('buttons.delete')}
            </Box>
          ),
          icon: (
            <Box>
              <DeleteSmallIcon />
            </Box>
          ),
          onItemClick: () => {
            setDeleteChatIsOpen(true, {
              ...session,
              isGroupError,
            });
          },
        },
      ].filter((item) => {
        if (item.value === 'delete' || item.value === 'clearHistory') {
          return true;
        }
        return !isGroupError;
      });
      return items;
    },
    [
      changePinnedList,
      client?.Api.session,
      pinnedSessions,
      sessionsCollapsed.length,
      setClearChatHistoryIsOpen,
      setDeleteChatIsOpen,
      t,
    ]
  );
  const latestRef = useLatest({ sessions });

  const getChatStatusThrottle = useMemo(
    () =>
      throttle(async () => {
        const { sessions } = latestRef.current;
        const length = sessions.length;
        const params: Record<string, string> = {};

        for (let i = length - 1 - pageSizeRef.current; i >= 0; i--) {
          const session = sessions[i];
          if (
            !session.user_info ||
            session.user_info.state === StateEnum.DELETE_ACCOUNT ||
            session.user_info?.account_type === AccountType.Robot ||
            isSessionCategory(session.chat_id, SessionCategoryPrefix.Group)
          ) {
            continue;
          }
          params[session.user_info.user_id] = session.chat_id;
        }

        if (Object.keys(params).length) {
          getChatStatus(params);
        }
        pageSizeRef.current = length;
      }, 3000),
    [getChatStatus, latestRef]
  );

  useLayoutEffect(() => {
    if (chat_id) {
      activeSession(chat_id);
    }
  }, [chat_id, activeSession, sessionsStatus]);

  useLayoutEffect(() => {
    setItemContentHeight(
      `calc(var(--font-sizes-xs) * 1.5 * ${chatLines === ChatLinesEnum.Two ? 1 : 2} + var(--font-sizes-sm) * 1.5)`
    );
  }, [chatLines]);

  useLayoutEffect(() => {
    if (
      activatedSessionId &&
      activatedSessionId !== chat_id &&
      activatedSessionId === mediaActivatedId
    ) {
      navigate(Paths.MESSAGES_CHATS + '/' + activatedSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activatedSessionId, navigate]);

  useLayoutEffect(() => {
    if (collapseChatItemActivated) {
      setShowCollapseChat(true);
    }
  }, [collapseChatItemActivated]);

  // double click nav scroll to unread message
  const chatListRef = useRef<ChatListRef>(null);
  const jumpIndex = useRef<number>(0);
  useEventListener(
    'dblclick',
    (e) => {
      if (!chatListRef?.current) return;
      if (showCollapseChat) return;
      e.preventDefault();
      e.stopPropagation();
      let scrollIndex = [...sessionsExpanded, ...sessionsExpanded].findIndex((session, i) => {
        if (i < jumpIndex.current) return false;
        const { unread_count, muted_unread_count } = getSessionUnreadCount(session);
        return unread_count > 0 || muted_unread_count > 0;
      });
      // if don't find index
      if (scrollIndex === -1) {
        jumpIndex.current = 0;
        return;
      }
      // if index in second array
      if (scrollIndex >= sessionsExpanded.length) scrollIndex -= sessionsExpanded.length;
      // scroll
      const res = chatListRef.current.scrollToIndex(scrollIndex);
      if (!res) return;
      jumpIndex.current = scrollIndex + 1;
    },
    {
      target: document.querySelector(`[data-path="${Paths.MESSAGES}"]`) ?? undefined,
    }
  );

  useLayoutEffect(() => {
    if (sessions.length && sessionsLoading) {
      getChatStatusThrottle();
    }
  }, [getChatStatusThrottle, sessions.length, sessionsLoading]);

  const items = useMemo(
    () => (!showCollapseChat ? sessionsExpanded : sessionsCollapsed),
    [showCollapseChat, sessionsExpanded, sessionsCollapsed]
  );
  const collapseChatItemActivated_ = useMemo(
    () => (!showCollapseChat ? collapseChatItemActivated : undefined),
    [showCollapseChat, collapseChatItemActivated]
  );

  return (
    <ChatSidebar
      show={!!user?.username && sessionsStatus > SessionsStatus.LOADING}
      showCollapseChat={showCollapseChat}
      onShowCollapseChat={(show) => {
        setShowCollapseChat(show);
      }}
      connecting={connecting}
      loading={sessionsLoading}
      selectedKey={activatedSessionId}
      onSelectedKeyChange={(key) => {
        const session = sessions.find((session) => session.chat_id === key);
        const isCollapseChat = session && session.chat_id === DefaultCollapseGroupId;
        if (isCollapseChat) {
          setShowCollapseChat(true);
          return;
        }
        if (session && session.user_info?.user_id) {
          fetchProfileUser(session.user_info.user_id, false);
        }
        navigate(Paths.MESSAGES_CHATS + '/' + key);
      }}
      packageError={myPackageError}
      onRetry={async () => {
        setRetryConnecting(true);
        await client?.Api.user.retryPackage();
        setRetryConnecting(false);
      }}
    >
      <ContextMenu itemsGetter={getItemsData}>
        <Box hidden={showCollapseChat}>
          <PinnedList />
        </Box>
        <ChatListWithData
          ref={chatListRef}
          items={items}
          itemContentHeight={itemContentHeight}
          showCollapseChat={showCollapseChat}
          collapseChatItemActivated={collapseChatItemActivated_}
        />
      </ContextMenu>
      <ConfirmDialog
        open={collapseConfirm.open}
        onClose={() => setCollapseConfirm({ open: false, session: null })}
        title={t('chatSettings.moveToCollapsedChats')}
        subTitle={t('chatSettings.moveToCollapsedChatsSubTitle')}
        onOk={async () => {
          if (collapseConfirm.session) {
            try {
              await changePinnedList(collapseConfirm.session?.chat_id, false, false);
              await client?.Api.session.setSessionCollapse(
                collapseConfirm.session.chat_id,
                'default_collapse_group'
              );
              tools.toaster.success({
                title: t('chatSettings.chatCollapsed'),
              });
              setCollapseConfirm({ open: false, session: null });
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
              tools.toaster.error({
                title: 'error',
              });
            }
          }
        }}
        okText={t('buttons.confirm')}
      />
    </ChatSidebar>
  );
});

export interface ChatListRef {
  scrollToIndex: (index: number) => boolean;
}

interface ChatListProps {
  items: Session[];
  itemContentHeight: string;
  showCollapseChat: boolean;
  collapseChatItemActivated?: boolean;
}

const ChatListWithData = forwardRef<ChatListRef, ChatListProps>(
  ({ items, itemContentHeight, showCollapseChat, collapseChatItemActivated }, ref) => {
    const containerRef = useRef<HTMLElement>(null);

    const virtualizer = useVirtualizer({
      count: items.length,
      getScrollElement: () => containerRef.current,
      getItemKey: (index) => items[index]?.chat_id ?? index,
      estimateSize: () => 74,
      overscan: 10,
    });

    const virtualItems = virtualizer.getVirtualItems();

    useImperativeHandle(
      ref,
      () => ({
        scrollToIndex: (index) => {
          if (virtualizer.isScrolling) return false;
          virtualizer.scrollToIndex(index, { align: 'start', behavior: 'smooth' });
          return true;
        },
      }),
      [virtualizer]
    );

    const [animate, setAnimate] = useState<boolean>(false);
    useEffect(() => {
      const id = setTimeout(() => {
        setAnimate(true);
      }, 100);
      return () => clearTimeout(id);
    }, []);

    return (
      <ChatList
        ref={containerRef}
        css={{
          '--chat-item-content-height': itemContentHeight,
        }}
        key={`${showCollapseChat}`}
        animation="fade-in 0.2s ease"
      >
        {items.length === 0 && <EmptyContact showCollapseChat={showCollapseChat} />}
        {items.length > 0 && (
          <Box h={`${virtualizer.getTotalSize()}px`} position="relative" width="100%">
            {virtualItems.map(({ key, index, start }) => {
              const session = items[index];
              const isCollapseChat = session && session.chat_id === DefaultCollapseGroupId;
              const active = collapseChatItemActivated && isCollapseChat ? true : undefined;
              return (
                <Box
                  key={key}
                  ref={virtualizer.measureElement}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  transform={`translateY(${start}px)`}
                  transition={animate ? 'transform 0.3s ease-out' : 'none'}
                >
                  <SessionItem
                    isCollapseChat={session.chat_id === DefaultCollapseGroupId}
                    session={session}
                    active={active}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </ChatList>
    );
  }
);
